import type { Middleware, PayloadAction, Reducer } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"

import type { DateRange } from "types/date.types"
import type { EntityType } from "types/common.types"
import { supersedeWave } from "features/api/requestQueue"
import type { RootState } from "."

/**
 * Every date range the app keeps. Entity pages use their own entity name, the
 * two remaining ones belong to screens that are not an entity dashboard.
 */
export const DATE_RANGE_KEYS = [
  "device",
  "events",
  "auditTrail",
  "powerline",
  "tower",
  "span",
] as const

export type DateRangeKey = (typeof DATE_RANGE_KEYS)[number]

/**
 * What a date range can be asked for: one of the keys above, or any entity.
 * Entities without a range of their own read the device one.
 */
export type DateRangeSource = DateRangeKey | EntityType

export const dateRangeKeyFor = (source: DateRangeSource): DateRangeKey =>
  (DATE_RANGE_KEYS as readonly string[]).includes(source)
    ? (source as DateRangeKey)
    : "device"

type DateRangeState = Record<DateRangeKey, DateRange | null>

// Written out so tsc fails when a key is added to DATE_RANGE_KEYS and not here.
const initialState: DateRangeState = {
  device: null,
  events: null,
  auditTrail: null,
  powerline: null,
  tower: null,
  span: null,
}

interface UpdateDateRangePayload {
  key: DateRangeKey
  dateRange: DateRange | null
}

export const dateRangeSlice = createSlice({
  name: "dateRange",
  initialState,
  reducers: {
    updateDateRange: (
      state,
      action: PayloadAction<UpdateDateRangePayload>,
    ): DateRangeState => ({
      ...state,
      [action.payload.key]: action.payload.dateRange,
    }),
  },
})

export const { updateDateRange } = dateRangeSlice.actions
export const dateRangeReducer: Reducer<DateRangeState> = dateRangeSlice.reducer
export default dateRangeReducer

/**
 * Drops whatever the range the user just left still had queued, so the store
 * never runs a query nobody is waiting for. It cancels by `toDate`, which is
 * what ties a telemetry request to its wave, but it watches both ends to decide
 * there is anything to cancel: a custom range edited only at the start keeps
 * its `toDate` and abandons its queued queries all the same.
 *
 * The old and new ranges share the wave key in that case, which is safe because
 * this runs inside the dispatch — the new range's requests are only enqueued
 * once React has re-rendered on it, and the queue holds none of them yet.
 *
 * On the store and not in a component effect on purpose: StrictMode remounts
 * every effect, so an effect that cancelled on cleanup would cancel its own
 * remount — the regression `bbdf6b8` reverted. A dispatch is fired once, by a
 * user who moved the range.
 */
export const dateRangeMiddleware: Middleware = (store) => (next) => (action) => {
  const isUpdate =
    typeof action === "object" &&
    action !== null &&
    "type" in action &&
    typeof action.type === "string" &&
    action.type.startsWith(`${dateRangeSlice.name}/`)
  if (!isUpdate) return next(action)

  const before = (store.getState() as RootState).dateRange
  const left = DATE_RANGE_KEYS.map((key) => before[key])
  const result = next(action)
  const after = (store.getState() as RootState).dateRange

  DATE_RANGE_KEYS.forEach((key, i) => {
    const was = left[i]
    if (!was) return
    const now = after[key]
    if (was.fromDate !== now?.fromDate || was.toDate !== now?.toDate) {
      supersedeWave(was.toDate)
    }
  })
  return result
}

export const selectDateRanges = (state: RootState) => state.dateRange
